import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../../utils/apiConfig';
import './UpcomingTasks.css';

const UpcomingTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: ''
  });
  const [userRole, setUserRole] = useState('customer');

  useEffect(() => {
    fetchTasks();
    checkUserRole();
  }, []);

  const checkUserRole = () => {
    const token = localStorage.getItem('token');
    const firebaseToken = localStorage.getItem('firebaseToken');
    
    if (firebaseToken) {
      // Use Firebase token for API calls
      setUserRole('customer'); // Default to customer for task creation
    } else if (token) {
      // Fallback to regular token
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'customer');
      } catch (error) {
        setUserRole('customer');
      }
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const firebaseToken = localStorage.getItem('firebaseToken');
      const token = localStorage.getItem('token');
      
      if (!firebaseToken && !token) {
        setTasks([]);
        return;
      }

      const authToken = firebaseToken || token;
              const response = await axios.get(`${API_CONFIG.BASE_URL}/api/tasks`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const tasksData = response.data?.tasks || response.data || [];
      const processedTasks = Array.isArray(tasksData) ? tasksData.map(task => ({
        ...task,
        id: task._id || task.id // Ensure we have both id and _id for compatibility
      })) : [];
      setTasks(processedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Set fallback data when API is unavailable
      setTasks([
        { 
          _id: '1', 
          title: 'Sample Task 1', 
          description: 'This is a sample task', 
          dueDate: new Date().toISOString(), 
          priority: 'medium', 
          status: 'pending' 
        },
        { 
          _id: '2', 
          title: 'Sample Task 2', 
          description: 'Another sample task', 
          dueDate: new Date().toISOString(), 
          priority: 'high', 
          status: 'pending' 
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    
    if (!newTask.title.trim() || !newTask.description.trim() || !newTask.dueDate) {
      alert('Please fill in all required fields (Title, Description, and Due Date)');
      return;
    }

    try {
      const firebaseToken = localStorage.getItem('firebaseToken');
      const token = localStorage.getItem('token');
      const authToken = firebaseToken || token;
      
      if (!authToken) {
        alert('Authentication token not found. Please log in again.');
        return;
      }
      
      console.log('🔍 Creating task with data:', newTask);
      console.log('🔍 Using token:', authToken ? 'Token exists' : 'No token');
      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/tasks`, {
        ...newTask,
        status: 'pending',
        createdAt: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      console.log('✅ Task created successfully:', response.data);
      
      // Use the correct ID field (_id instead of id)
      const createdTask = {
        ...response.data,
        id: response.data._id || response.data.id // Ensure we have both for compatibility
      };
      
      setTasks([...tasks, createdTask]);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: ''
      });
      setShowAddForm(false);
      alert('Task created successfully!');
    } catch (error) {
      console.error('❌ Error adding task:', error);
      console.error('❌ Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || 'Error creating task. Please try again.';
      alert(`Error creating task: ${errorMessage}`);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const firebaseToken = localStorage.getItem('firebaseToken');
      const token = localStorage.getItem('token');
      const authToken = firebaseToken || token;
      
      console.log('🔍 Deleting task with ID:', taskId);
      
      await axios.delete(`${API_CONFIG.BASE_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      // Filter by both id and _id for compatibility
      setTasks(tasks.filter(task => 
        (task.id !== taskId) && (task._id !== taskId)
      ));
      
      alert('Task deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      console.error('❌ Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting task. Please try again.';
      alert(`Error deleting task: ${errorMessage}`);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const firebaseToken = localStorage.getItem('firebaseToken');
      const token = localStorage.getItem('token');
      const authToken = firebaseToken || token;
      
      console.log('🔍 Completing task with ID:', taskId);
      
      await axios.put(`${API_CONFIG.BASE_URL}/api/tasks/${taskId}`, {
        status: 'completed'
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      // Update the task in the local state - check both id and _id
      setTasks(tasks.map(task => 
        (task.id === taskId || task._id === taskId)
          ? { ...task, status: 'completed' }
          : task
      ));
      
      alert('Task marked as completed!');
    } catch (error) {
      console.error('❌ Error completing task:', error);
      console.error('❌ Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || error.message || 'Error completing task. Please try again.';
      alert(`Error completing task: ${errorMessage}`);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <div className="upcoming-tasks-container">
      <div className="tasks-header">
        <h4>Upcoming Tasks</h4>
        {userRole === 'customer' && (
          <button 
            className="add-task-btn"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : '+ Add Task'}
          </button>
        )}
      </div>

      {showAddForm && (
        <form className="add-task-form" onSubmit={handleAddTask}>
          <input
            type="text"
            placeholder="Task Title"
            value={newTask.title}
            onChange={(e) => setNewTask({...newTask, title: e.target.value})}
            required
          />
          <textarea
            placeholder="Task Description"
            value={newTask.description}
            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
            required
          />
          <div className="form-row">
            <select
              value={newTask.priority}
              onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
            >
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
            <input
              type="date"
              value={newTask.dueDate}
              onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
              required
            />
          </div>
          <button type="submit" className="submit-task-btn">Create Task</button>
        </form>
      )}

      <div className="tasks-list">
        {loading ? (
          <p>Loading tasks...</p>
        ) : tasks.length === 0 ? (
          <p className="no-tasks">No upcoming tasks</p>
        ) : (
                     tasks.map((task) => {
            // Use _id as primary key, fallback to id
            const taskId = task._id || task.id;
            return (
              <div key={taskId} className={`task-item ${task.status === 'completed' ? 'completed' : ''}`}>
                <div className="task-header">
                  <h5 className="task-title">{task.title}</h5>
                  <div className="task-badges">
                    <span 
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      {task.priority}
                    </span>
                    {task.status === 'completed' && (
                      <span className="status-badge completed">
                        ✓ Completed
                      </span>
                    )}
                  </div>
                </div>
                <p className="task-description">{task.description}</p>
                <div className="task-footer">
                  <span className="task-date">Due: {formatDate(task.dueDate)}</span>
                  <div className="task-actions">
                    {task.status !== 'completed' && (
                      <button 
                        className="complete-task-btn"
                        onClick={() => handleCompleteTask(taskId)}
                      >
                        Complete
                      </button>
                    )}
                    <button 
                      className="delete-task-btn"
                      onClick={() => handleDeleteTask(taskId)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UpcomingTasks;
